// Bugfix 22 (#1392): Nieuwe trainer toevoegen. Eén formulier in drie stappen:
// basisgegevens en rechten naast elkaar, daarna het controlescherm. Terug gaan
// wisselt alleen de weergave, dus niets van wat is ingevuld raakt kwijt.
// De rechtenblokken zelf (openklappen, Alle rechten, tellers, Standaardrol
// toepassen) doet rechten_controller.js, dezelfde als in Rollen & Rechten.
import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "formulier", "stap", "invoer", "invoerVoet", "controle", "foutblok", "foutlijst", "verplicht",
    "account", "functie", "startdatum", "beveiligd",
    "uitnodiging", "wachtwoordViaEmail", "directActiveren", "inactief",
    "uitnodigingLabel", "wachtwoordViaEmailLabel", "directActiverenLabel", "inactiefLabel",
    "wachtwoordBlok", "wachtwoord", "volgende",
    "sInitialen", "sNaam", "sEmail", "sFunctie", "sAccount", "sStart", "sStartLabel", "sRechten", "sUitnodiging",
    "aanmaakKnop", "aanmaakTekst"
  ]

  static values = { teksten: Object, controleUrl: String }

  connect() {
    this.schakelBij()
    this.zetStap(1)
  }

  // ---- stappen ---------------------------------------------------------------

  zetStap(nummer) {
    this.huidigeStap = nummer
    this.stapTargets.forEach((stap) => {
      const nr = Number(stap.dataset.stap)
      stap.dataset.stand = nr < nummer ? "klaar" : (nr === nummer ? "actief" : "open")
      if (nr === nummer) stap.setAttribute("aria-current", "step")
      else stap.removeAttribute("aria-current")
    })
  }

  inBasis() {
    if (this.huidigeStap !== 3) this.zetStap(1)
  }

  inRechten() {
    if (this.huidigeStap !== 3) this.zetStap(2)
  }

  // ---- accountinstellingen ---------------------------------------------------

  // Uitnodigen en direct activeren sluiten elkaar uit; het wachtwoord via de
  // mail hoort alleen bij een uitnodiging, en een eigen wachtwoord alleen bij
  // direct activeren.
  schakel(event) {
    const gewijzigd = event.currentTarget
    if (gewijzigd === this.directActiverenTarget && gewijzigd.checked) {
      this.uitnodigingTarget.checked = false
    }
    if (gewijzigd === this.uitnodigingTarget && gewijzigd.checked) {
      this.directActiverenTarget.checked = false
      this.wachtwoordViaEmailTarget.checked = true
    }
    this.schakelBij()
  }

  schakelBij() {
    if (!this.hasUitnodigingTarget) return
    const uitnodigen = this.uitnodigingTarget.checked
    const direct = this.directActiverenTarget.checked
    this.wachtwoordViaEmailTarget.disabled = !uitnodigen
    if (!uitnodigen) this.wachtwoordViaEmailTarget.checked = false
    this.wachtwoordViaEmailLabelTarget.classList.toggle("is-uit", !uitnodigen)
    this.wachtwoordBlokTarget.hidden = !direct
  }

  // ---- naar het controlescherm -----------------------------------------------

  async naarControle() {
    this.wisFouten()
    const leeg = this.verplichtTargets.filter((veld) => veld.value.trim() === "")
    if (leeg.length > 0) {
      leeg.forEach((veld) => this.toonVeldFout(veld.dataset.veld, veld.validationMessage || ""))
      leeg[0].focus()
      this.zetStap(1)
      return
    }

    const knop = this.volgendeTarget
    const origineel = knop.innerHTML
    knop.disabled = true
    knop.textContent = this.teksten.controleren
    try {
      const antwoord = await fetch(this.controleUrlValue, {
        method: "POST",
        body: new FormData(this.formulierTarget),
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken() },
        credentials: "same-origin"
      })
      if (!antwoord.ok) throw new Error(`status ${antwoord.status}`)
      const uitslag = await antwoord.json()
      if (!uitslag.geldig) {
        this.toonFouten(uitslag.fouten || {})
        return
      }
    } catch (fout) {
      // Controleren lukt niet (netwerk): toch door. Bij aanmaken controleert
      // de server alles opnieuw en toont hij de fouten op deze pagina.
      console.warn("Controle nieuwe trainer mislukt", fout)
    } finally {
      knop.disabled = false
      knop.innerHTML = origineel
    }

    this.vulSamenvatting()
    this.invoerTarget.hidden = true
    this.invoerVoetTarget.hidden = true
    this.controleTarget.hidden = false
    this.zetStap(3)
    this.element.scrollIntoView({ behavior: "smooth", block: "start" })
    this.controleTarget.focus({ preventScroll: true })
  }

  terug() {
    this.controleTarget.hidden = true
    this.invoerTarget.hidden = false
    this.invoerVoetTarget.hidden = false
    this.zetStap(2)
    this.element.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  aanmaken(event) {
    // Enter in een veld van stap 1 verstuurt niet meteen: eerst controleren.
    if (this.controleTarget.hidden) {
      event.preventDefault()
      this.naarControle()
      return
    }
    this.aanmaakKnopTarget.disabled = true
    this.aanmaakTekstTarget.textContent = this.teksten.bezig
  }

  // ---- samenvatting ------------------------------------------------------------

  vulSamenvatting() {
    const waarde = (naam) => {
      const veld = this.formulierTarget.elements.namedItem(naam)
      return veld && veld.value ? veld.value.trim() : ""
    }
    const naam = [waarde("first_name"), waarde("last_name")].filter(Boolean).join(" ")
    this.sNaamTarget.textContent = naam
    this.sEmailTarget.textContent = waarde("email")
    const achternaamDelen = waarde("last_name").split(" ")
    this.sInitialenTarget.textContent = [waarde("first_name").charAt(0), achternaamDelen[achternaamDelen.length - 1].charAt(0)]
      .filter(Boolean).join("").toUpperCase()

    const functie = this.functieTarget
    this.sFunctieTarget.textContent = functie.value ? functie.options[functie.selectedIndex].text.trim() : this.teksten.geen_functie

    const account = this.accountTarget
    const gekozenAccount = account.options[account.selectedIndex]
    this.sAccountTarget.textContent = gekozenAccount ? gekozenAccount.text.trim() : ""

    const start = this.startdatumTarget.value
    this.sStartTarget.hidden = !start
    this.sStartLabelTarget.hidden = !start
    if (start) {
      const [jaar, maand, dag] = start.split("-")
      this.sStartTarget.textContent = `${dag}-${maand}-${jaar}`
    }

    this.vulRechten()
    this.vulUitnodiging()
  }

  vulRechten() {
    const lijst = this.sRechtenTarget
    lijst.innerHTML = ""
    let totaalAan = 0
    this.element.querySelectorAll("[data-rechten-target='categorie']").forEach((categorie) => {
      const rechten = Array.from(categorie.querySelectorAll("[data-rechten-target='recht']"))
      if (rechten.length === 0) return
      const aan = rechten.filter((recht) => recht.checked).length
      if (aan === 0) return
      totaalAan += aan
      const regel = document.createElement("li")
      const naam = document.createElement("span")
      naam.textContent = `${categorie.dataset.naam}:`
      const teller = document.createElement("span")
      teller.className = "nt-teller-klein"
      teller.textContent = `${aan}/${rechten.length}`
      regel.append(naam, teller)
      lijst.append(regel)
    })
    if (totaalAan === 0) {
      const regel = document.createElement("li")
      regel.className = "nt-teller-klein"
      regel.textContent = this.teksten.geen_rechten
      lijst.append(regel)
    }
    this.beveiligdTargets.filter((vink) => vink.checked).forEach((vink) => {
      const regel = document.createElement("li")
      regel.textContent = vink.dataset.label
      regel.style.fontWeight = "600"
      lijst.append(regel)
    })
  }

  vulUitnodiging() {
    const lijst = this.sUitnodigingTarget
    lijst.innerHTML = ""
    const regels = []
    const uitnodigen = this.uitnodigingTarget.checked
    if (uitnodigen) {
      regels.push(this.teksten.per_email)
      regels.push(this.wachtwoordViaEmailTarget.checked ? this.teksten.zelf_wachtwoord : this.teksten.tijdelijk_wachtwoord)
    }
    if (this.directActiverenTarget.checked) regels.push(this.teksten.direct)
    if (this.inactiefTarget.checked) regels.push(this.teksten.inactief)
    regels.forEach((tekst) => {
      const regel = document.createElement("li")
      const vink = document.createElement("span")
      vink.className = "nt-vinkje"
      vink.textContent = "✓"
      regel.append(vink, document.createTextNode(` ${tekst}`))
      lijst.append(regel)
    })
    this.aanmaakTekstTarget.textContent = uitnodigen ? this.teksten.aanmaken_uitnodigen : this.teksten.aanmaken
  }

  // ---- fouten ----------------------------------------------------------------

  toonFouten(fouten) {
    const teksten = []
    Object.entries(fouten).forEach(([veld, meldingen]) => {
      const lijst = Array.isArray(meldingen) ? meldingen : [meldingen]
      teksten.push(...lijst)
      this.toonVeldFout(veld, lijst.join(" "))
    })
    this.foutlijstTarget.innerHTML = ""
    teksten.forEach((tekst) => {
      const regel = document.createElement("li")
      regel.textContent = tekst
      this.foutlijstTarget.append(regel)
    })
    this.foutblokTarget.hidden = teksten.length === 0
    if (teksten.length > 0) this.foutblokTarget.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  toonVeldFout(veld, tekst) {
    const blok = this.element.querySelector(`[data-fout-veld='${veld}']`)
    if (blok) blok.classList.add("heeft-fout")
    const plek = this.element.querySelector(`[data-fout-voor='${veld}']`)
    if (plek && tekst) plek.textContent = tekst
  }

  wisFouten() {
    this.element.querySelectorAll(".heeft-fout").forEach((blok) => blok.classList.remove("heeft-fout"))
    this.element.querySelectorAll("[data-fout-voor]").forEach((plek) => { plek.textContent = "" })
    this.foutblokTarget.hidden = true
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }

  get teksten() {
    return this.tekstenValue || {}
  }
}
